import React, { useState, useEffect, Fragment } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
  Tooltip,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useAdd from "../../../../api/useAdd";
import { useNavigate } from "react-router-dom";

const ExpensePayment = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    expense_source: null,
    amount: "",
    date: new Date().toISOString().split("T")[0],
  });
  const [formErrors, setFormErrors] = useState({});
  const [sourceData, setSourceData] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);

  // const { data: sources, loading: loadingSources } = useFetchObjects(
  //   ["expense-sources"],
  //   `expense-sources/`,
  //   token
  // );

  const {
    handleAdd,
    resetForm,
    loading: saving,
  } = useAdd(
    "expense-payments",
    token,
    // t("finance.paymentSuccess"),
    t("finance.paymentFailed")
  );

  // useEffect(() => {
  //   if (sources) {
  //     setSourceData(
  //       sources.map((source) => ({
  //         id: source.id,
  //         label: source.name,
  //         total_paid: source.total_paid,
  //         remaining_balance: source.remaining_balance,
  //       }))
  //     );
  //   }
  // }, [sources]);

  useEffect(() => {
    if (resetForm) {
      setFormData({
        expense_source: null,
        amount: "",
        date: new Date().toISOString().split("T")[0],
      });
      setFormErrors({});
      setSelectedSource(null);
    }
  }, [resetForm]);

  const handleSave = (e) => {
    e.preventDefault();
    const errors = {};

    // if (!formData.expense_source) {
    //   errors.expense_source = t("finance.sourceRequired");
    // }
    if (!formData.amount || formData.amount <= 0) {
      errors.amount = t("finance.amountRequired");
    }

    // else if (
    //   selectedSource &&
    //   parseFloat(formData.amount) > parseFloat(selectedSource.remaining_balance)
    // ) {
    //   errors.amount = t("finance.amountExceedsBalanceDue");
    // }

    if (!formData.date) {
      errors.date = t("finance.dateRequired");
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
    } else {
      handleAdd(formData);
    }
  };

  // const handleSourceSelect = (value) => {
  //   setFormData({ ...formData, expense_source: value?.id || null });
  //   setSelectedSource(value);
  //   setFormErrors({ ...formErrors, expense_source: "" });
  // };

  return (
    <Fragment>
      <Box
        sx={{
          background: "linear-gradient(to bottom, #f9fafa, #ffffff)",
          py: 4,
          minHeight: "60vh",
          width: "100%",
        }}
      >
        <Box display="flex" alignItems="center" mb={4}>
          <Tooltip title={t("finance.goBack")} arrow>
            <IconButton
              onClick={() => navigate(-1)}
              aria-label={t("finance.goBack")}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon size="large" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box mb={6} textAlign="center">
          <Typography
            variant="h3"
            fontWeight={700}
            color="primary"
            gutterBottom
            sx={{ fontSize: isSmallScreen ? "1.8rem" : "2.5rem" }}
          >
            {t("finance.expensePayment")}
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{ fontSize: isSmallScreen ? "0.9rem" : "1rem" }}
          >
            {t("finance.paymentSubtitle")}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: 3,
                boxShadow: 3,
                p: 3,
              }}
            >
              <Typography variant="h5" fontWeight={600} color="primary" mb={2}>
                {t("finance.paymentForm")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <form onSubmit={handleSave}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {/* {loadingSources ? (
                      <CircularProgress />
                    ) : (
                      <Autocomplete
                        options={sourceData}
                        getOptionLabel={(option) => option.label}
                        onChange={(e, value) => handleSourceSelect(value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("finance.selectSource")}
                            variant="outlined"
                            error={!!formErrors.expense_source}
                            helperText={formErrors.expense_source}
                            fullWidth
                          />
                        )}
                      />
                    )} */}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("finance.amountPaid")}
                      variant="outlined"
                      fullWidth
                      type="number"
                      inputProps={{ step: "0.01", min: "0" }}
                      value={formData.amount}
                      onChange={(e) =>
                        setFormData({ ...formData, amount: e.target.value })
                      }
                      error={!!formErrors.amount}
                      helperText={formErrors.amount}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("finance.paymentDate")}
                      variant="outlined"
                      fullWidth
                      type="date"
                      value={formData.date}
                      onChange={(e) =>
                        setFormData({ ...formData, date: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      error={!!formErrors.date}
                      helperText={formErrors.date}
                    />
                  </Grid>
                </Grid>
                <Box mt={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={saving}
                  >
                    {saving ? (
                      <CircularProgress size={20} />
                    ) : (
                      t("finance.save")
                    )}
                  </Button>
                </Box>
              </form>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{ backgroundColor: "#f5f5f5", borderRadius: 3, boxShadow: 3 }}
            >
              <CardContent>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="primary"
                  mb={2}
                >
                  {t("finance.sourceDetails")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {selectedSource ? (
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("finance.sourceName")}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {selectedSource.label}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("finance.totalPaid")}
                      </Typography>
                      <Typography variant="h6">
                        {selectedSource.total_paid} ؋
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("finance.remainingBalance")}
                      </Typography>
                      <Typography variant="h6" color="error">
                        {selectedSource.remaining_balance} ؋
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Typography textAlign="center" color="textSecondary">
                    {t("finance.noSourceSelected")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Fragment>
  );
};

export default ExpensePayment;
